import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Card, Badge, Button, SearchInput, Toast } from '../ui';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import ImageGenModal from './ImageGenModal';
import {
  listImageCandidates,
  commitImage,
  rejectImage,
  imageCandidateUrl,
  assetUrl,
} from '../../services/pimApi';
import './pim.css';

/*
 * Review queue for AI-generated product images. Bulk jobs stage candidates as
 * 'pending'; this grid lets a reviewer approve or reject them, individually or
 * in bulk (bulk approve targets the primary image; the modal offers a
 * primary-vs-gallery choice per candidate). Clicking a card opens the full
 * side-by-side review modal (reference vs generated vs current image).
 */
export default function ImageReviewQueue() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [skuDraft, setSkuDraft] = useState('');
  const skuFilter = useDebouncedValue(skuDraft);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [reviewing, setReviewing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const limit = 60;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (status) params.status = status;
      if (skuFilter) params.sku = skuFilter;
      const data = await listImageCandidates(params);
      setItems(data.items);
      setTotal(data.total);
      setSelected(new Set());
    } catch (e) {
      setToast({ message: 'Failed to load candidates', error: true });
    } finally {
      setLoading(false);
    }
  }, [status, skuFilter, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    const verb = action === 'approve' ? 'Approve' : 'Reject';
    if (!window.confirm(`${verb} ${selected.size} candidate(s)?`)) return;
    setBusy(true);
    let ok = 0;
    let failed = 0;
    for (const id of selected) {
      try {
        if (action === 'approve') await commitImage(id);
        else await rejectImage(id);
        ok += 1;
      } catch (e) {
        failed += 1;
      }
    }
    setBusy(false);
    setToast({
      message: `${verb}d ${ok} candidate(s)${failed ? ` · ${failed} failed` : ''}`,
      error: failed > 0,
    });
    load();
  };

  return (
    <>
      <PageHeader
        title="Image review"
        sub="AI-generated product images awaiting approval. Open a candidate to set it as the primary image or add it to the gallery; bulk approve sets primary."
        crumbs={[{ label: 'PIM', to: '/pim/products' }]}
        actions={
          <Button variant="secondary" to="/pim/products">
            Back to products
          </Button>
        }
      />

      {selected.size > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>{selected.size} selected</strong>
            <Button size="sm" onClick={() => bulkAction('approve')} disabled={busy}>
              Approve as primary
            </Button>
            <Button size="sm" variant="secondary" onClick={() => bulkAction('reject')} disabled={busy}>
              Reject selected
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        </Card>
      )}

      <Card flush>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: 14, alignItems: 'center' }}>
          <SearchInput
            value={skuDraft}
            onChange={(v) => { setOffset(0); setSkuDraft(v); }}
            placeholder="Filter by SKU — wildcards * and ?"
          />
          <select value={status} onChange={(e) => { setOffset(0); setStatus(e.target.value); }}>
            <option value="pending">Pending review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
          <span style={{ fontSize: 13, color: '#667' }}>{total} candidate(s)</span>
        </div>

        <div style={{ padding: 14 }}>
          {loading && <p className="pim-muted">Loading…</p>}
          {!loading && items.length === 0 && (
            <p className="pim-muted">
              No candidates here. Generate images from the{' '}
              <Link to="/pim/products">product list</Link> (bulk) or a product editor (single SKU).
            </p>
          )}
          <div className="pim-imgrev-grid">
            {items.map((c) => (
              <div key={c.id} className="pim-imgrev-card" onClick={() => setReviewing(c)}>
                <div style={{ position: 'relative' }}>
                  <img src={imageCandidateUrl(c.id)} alt={c.sku} loading="lazy" />
                  {c.status === 'pending' && (
                    <input
                      type="checkbox"
                      className="pim-imgen-badge"
                      style={{ position: 'absolute', top: 8, left: 8, width: 18, height: 18 }}
                      checked={selected.has(c.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggle(c.id)}
                      aria-label={`Select ${c.sku}`}
                    />
                  )}
                </div>
                <div className="pim-imgrev-meta">
                  <strong>
                    <code>{c.sku}</code>
                  </strong>
                  {c.product_name && <span>{c.product_name}</span>}
                  <span>
                    {c.reference_source ? `ref: ${c.reference_source} · ` : ''}
                    {new Date(c.created_at).toLocaleString()}
                  </span>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {c.status === 'pending' && <Badge variant="warning">Pending</Badge>}
                    {c.status === 'approved' && <Badge variant="success">Approved</Badge>}
                    {c.status === 'rejected' && <Badge variant="neutral">Rejected</Badge>}
                    {c.has_primary_image && c.status === 'pending' && (
                      <Badge variant="info">has image</Badge>
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 14 }}>
          <span style={{ fontSize: 13, color: '#667' }}>
            showing {total ? offset + 1 : 0}–{Math.min(offset + limit, total)} of {total}
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
              Prev
            </Button>
            <Button variant="secondary" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>
              Next
            </Button>
          </span>
        </div>
      </Card>

      {reviewing && (
        <ImageGenModal
          sku={reviewing.sku}
          candidate={reviewing}
          currentImageUrl={
            reviewing.has_primary_image && reviewing.asset_id == null && reviewing.status === 'pending'
              ? null // asset id of the existing image is not in the list payload; modal shows reference vs generated
              : reviewing.asset_id
              ? assetUrl(reviewing.asset_id)
              : null
          }
          onClose={() => setReviewing(null)}
          onCommitted={(result) => {
            if (result?.committed)
              setToast({
                message: `${reviewing.sku}: image ${
                  result.destination === 'gallery' ? 'added to gallery' : 'set as primary image'
                }`,
              });
            if (result?.rejected) setToast({ message: `${reviewing.sku}: candidate rejected` });
            load();
          }}
        />
      )}

      <Toast message={toast?.message} error={toast?.error} />
    </>
  );
}
